import type { FootprintReasonCode, OnboardingSessionConfig } from '@onefootprint/request-types';
import type { IdDocOutcome, OverallOutcome } from '@onefootprint/types';
import { assign, createMachine } from 'xstate';
import { getLogger } from '@/idv/utils';
import type { CommonIdvContext } from '@/idv/utils/state-machine';
import { getRequirements } from '../get-requirement';
import isRepeatRequirement, { requirementKey } from '../is-repeat-requirement';
import {
  getPreferUploadDoc,
  NextRequirementTargets,
  requirementCompletedTransition,
  shouldWaitForComponentsSdk,
} from './machine.utils';
import type { MachineContext, MachineEvents } from './types';

export type OnboardingMachineArgs = {
  config: OnboardingSessionConfig;
  idvContext: Omit<CommonIdvContext, 'backContext'>;
  idDocOutcome?: IdDocOutcome;
  overallOutcome?: OverallOutcome;
  fixtureRiskSignals?: FootprintReasonCode[];
};

const { logWarn } = getLogger({ location: 'onboarding-requirements' });

const createOnboardingMachine = ({
  config,
  idvContext,
  idDocOutcome,
  overallOutcome,
  fixtureRiskSignals,
}: OnboardingMachineArgs) =>
  createMachine(
    {
      predictableActionArguments: true,
      id: 'onboarding-requirements',
      schema: {
        context: {} as MachineContext,
        events: {} as MachineEvents,
      },
      // eslint-disable-next-line @typescript-eslint/consistent-type-imports
      tsTypes: {} as import('./machine.typegen').Typegen0,
      initial: 'init',
      context: {
        idvContext,
        onboardingContext: {
          config,
          idDocOutcome,
          overallOutcome,
          fixtureRiskSignals,
        },
        requirements: [],
        handledRequirements: [],
      },
      on: {
        error: {
          target: 'error',
          actions: 'setError',
        },
        goBackRequirementsReceived: {
          target: 'checkRequirements',
          actions: ['assignRequirementsBack'],
        },
      },
      states: {
        init: {
          always: [
            {
              target: 'startOnboarding',
              cond: ctx => !ctx.idvContext.isTransfer,
            },
            { target: 'checkRequirements' },
          ],
        },
        startOnboarding: {
          on: {
            initialized: [
              {
                target: 'waitForComponentsSdk',
                cond: shouldWaitForComponentsSdk,
                description:
                  'If we are running bifrost for the components SDK, wait for the components SDK to finish handling its requirements',
              },
              {
                target: 'checkRequirements',
              },
            ],
          },
        },
        checkRequirements: {
          on: {
            onboardingRequirementsReceived: {
              target: 'router',
              actions: ['assignMissingRequirements'],
            },
          },
        },
        router: {
          always: NextRequirementTargets,
        },
        waitForComponentsSdk: {
          on: requirementCompletedTransition([]),
        },
        liveness: {
          on: requirementCompletedTransition(['markLastHandledRequirement']),
        },
        idDoc: {
          on: requirementCompletedTransition(['markLastHandledRequirement']),
        },
        sleep: {
          on: requirementCompletedTransition(['markLastHandledRequirement']),
        },
        process: {
          on: requirementCompletedTransition(['markLastHandledRequirement']),
        },
        error: {
          type: 'final',
        },
        success: {
          type: 'final',
        },
      },
    },
    {
      actions: {
        assignMissingRequirements: assign((ctx, event) => {
          const currentRequirement = event.payload.allRequirements[0];
          const isRepeat = isRepeatRequirement(ctx.lastHandledRequirement, currentRequirement);

          const isPreferUpload =
            currentRequirement?.kind === 'collect_document' && currentRequirement?.uploadSettings === 'prefer_upload';
          // when the playbook has the preferUpload setting, we show the button "continue on mobile", which moves the user to the handoff but the requirement is still the same
          // in this case, we don't want to log as an error
          if (isRepeat && !isPreferUpload) {
            // Keys, not the requirement objects: those carry the full page/section config.
            const lastKey = ctx.lastHandledRequirement ? requirementKey(ctx.lastHandledRequirement) : 'none';
            const nextKey = currentRequirement ? requirementKey(currentRequirement) : 'none';
            logWarn(`User is stuck on a requirement: ${lastKey} -> ${nextKey}`);
          }
          return {
            ...ctx,
            requirements: [...event.payload.allRequirements],
          };
        }),
        assignRequirementsBack: assign(ctx => {
          const lastRequirement = ctx.requirements[0];
          const handledRequirements = ctx.handledRequirements.filter(
            // Remove the current requirement from the list of handled requirements. This allows
            // us to revisit it when progressing forward
            requirement => requirementKey(lastRequirement) !== requirement,
          );

          return {
            ...ctx,
            handledRequirements,
          };
        }),
        markLastHandledRequirement: assign(ctx => {
          let lastHandledRequirement = ctx.requirements[0];
          if (lastHandledRequirement?.kind === 'collect_document') {
            const idDocReqs = getRequirements(ctx.requirements, 'collect_document');
            lastHandledRequirement = getPreferUploadDoc(idDocReqs) || idDocReqs[0];
          }
          const handledRequirements = [...ctx.handledRequirements, requirementKey(lastHandledRequirement)];
          return { ...ctx, lastHandledRequirement, handledRequirements };
        }),
        setError: assign((ctx, event) => {
          logWarn('Encountered error state in onboarding requirements machine', event?.payload?.error);
          return { ...ctx, error: event?.payload?.error };
        }),
      },
    },
  );

export default createOnboardingMachine;
